import ui from './ui.module.css';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { isIos, isStandalone } from '../lib/install';
import { scannedInviteCode } from '../lib/inviteLink';
import { cameraProblem, createQrDetector, openCamera, stopStream, type CameraProblem, type QrDetector } from '../lib/qrScan';
import { useLauncherPage } from './hooks';
import styles from './Scan.module.css';

const SCAN_INTERVAL_MS = 150;
const REJECTED_MS = 2500;

/**
 * Route "/join/scan" — the camera full screen with a viewfinder. The first invite link for this
 * origin (or bare invite code) it sees opens `/join/<code>`; any other QR code says so and scanning
 * goes on. The camera stops on Cancel, on leaving the page and whenever the page is hidden.
 */
export function Scan() {
  useLauncherPage();
  const navigate = useNavigate();
  const location = useLocation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [attempt, setAttempt] = useState(0);
  const [scanning, setScanning] = useState(false);
  const [problem, setProblem] = useState<CameraProblem | null>(null);
  const [rejectedAt, setRejectedAt] = useState(0);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    let alive = true;
    let starting = false;
    let stream: MediaStream | null = null;
    let detector: Promise<QrDetector> | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let lastRejected = 0;

    const stop = () => {
      clearTimeout(timer);
      stopStream(stream);
      stream = null;
      video.srcObject = null;
      setScanning(false);
    };

    const tick = async () => {
      if (!alive || !stream || !detector) return;
      let values: string[] = [];
      try {
        values = await (await detector).detect(video);
      } catch {
        // a frame that could not be read (not ready yet, resized): try the next one
      }
      if (!alive || !stream) return;
      for (const value of values) {
        const code = scannedInviteCode(value, window.location.origin);
        if (code) {
          alive = false;
          stop();
          navigate(`/join/${code}`, { replace: true });
          return;
        }
      }
      if (values.length > 0 && Date.now() - lastRejected > 500) {
        lastRejected = Date.now();
        setRejectedAt(lastRejected);
      }
      timer = setTimeout(() => void tick(), SCAN_INTERVAL_MS);
    };

    const start = async () => {
      if (!alive || starting || stream || document.hidden) return;
      starting = true;
      try {
        const opened = await openCamera();
        if (!alive || document.hidden) {
          stopStream(opened);
          return;
        }
        stream = opened;
        video.srcObject = opened;
        await video.play().catch(() => {});
        detector ??= createQrDetector();
        await detector;
        if (!alive || stream !== opened) return;
        setProblem(null);
        setScanning(true);
        void tick();
      } catch (err) {
        if (!alive) return;
        stop();
        setProblem(cameraProblem(err));
      } finally {
        starting = false;
      }
    };

    const onVisibility = () => {
      if (document.hidden) stop();
      else void start();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', stop);
    void start();
    return () => {
      alive = false;
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', stop);
      stop();
    };
  }, [attempt]);

  useEffect(() => {
    if (rejectedAt === 0) return;
    const id = setTimeout(() => setRejectedAt(0), REJECTED_MS);
    return () => clearTimeout(id);
  }, [rejectedAt]);

  const cancel = () => {
    if (location.key !== 'default') navigate(-1);
    else navigate('/join', { replace: true });
  };

  return (
    <div className={styles.scan} data-testid="scan">
      <video ref={videoRef} className={styles.video} playsInline muted autoPlay aria-hidden />
      <div className={styles.overlay}>
        <header className={styles.top}>
          <button className={styles.cancel} onClick={cancel}>
            Cancel
          </button>
        </header>
        <div className={styles.frame} aria-hidden>
          <span className={styles.corners} />
        </div>
        <p className={`${styles.hint} ${rejectedAt ? styles.hintRejected : ''}`} role="status" aria-live="polite">
          {rejectedAt
            ? "That's not a Bolt Circles invite"
            : scanning
              ? 'Scan the invite QR code'
              : problem
                ? ''
                : 'Starting the camera…'}
        </p>
      </div>
      {problem && (
        <div className={styles.problem}>
          <main className={ui.page}>
            <div className={ui.center}>
              <CameraProblemText problem={problem} />
              <div className={ui.actions}>
                {problem !== 'no-camera' && (
                  <button className={ui.primary} onClick={() => setAttempt((n) => n + 1)}>
                    Try again
                  </button>
                )}
                <button className={ui.secondary} onClick={() => navigate('/join', { replace: true })}>
                  Paste the link instead
                </button>
              </div>
            </div>
          </main>
        </div>
      )}
    </div>
  );
}

function CameraProblemText({ problem }: { problem: CameraProblem }) {
  if (problem === 'denied') {
    const how = isIos()
      ? isStandalone()
        ? 'Open iOS Settings → Bolt Circles → Camera and turn it on, then come back and tap Try again.'
        : 'In Safari, tap the aA button in the address bar → Website Settings → Camera → Allow, then tap Try again.'
      : "Allow the camera for this site in your browser's settings (usually the icon next to the address), then tap Try again.";
    return (
      <>
        <h1 className={ui.title}>Bolt Circles can't use the camera</h1>
        <p className={ui.sub}>{how}</p>
        <p className={ui.sub}>Or paste the invite link instead.</p>
      </>
    );
  }
  if (problem === 'no-camera') {
    return (
      <>
        <h1 className={ui.title}>No camera found</h1>
        <p className={ui.sub}>This device has no camera Bolt Circles can use. Paste the invite link instead.</p>
      </>
    );
  }
  return (
    <>
      <h1 className={ui.title}>The camera didn't start</h1>
      <p className={ui.sub}>Another app may be using it. Close that app and tap Try again, or paste the invite link.</p>
    </>
  );
}
